import { useState } from 'react';
import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';
import {
  DestinationPicker,
  EMPTY_DESTINATION,
  isDestinationReady,
  type Destination,
} from '@/features/destination/DestinationPicker';
import { useRecentProjectsStore } from '@/stores/recentProjectsStore';
import { closeComposeWindow } from '@/services/office/composeContext';
import { useSendAndFile, type SendAndFileResult } from './useSendAndFile';

// Le volet ENTIER quand Outlook l'ouvre sur un message en cours de rédaction.
//
// Ni onglets, ni recherche, ni arborescence : dans une fenêtre de rédaction le
// volet n'a qu'une seule chose à faire, et lui laisser sa panoplie de navigation
// donnerait à croire qu'on peut y consulter l'archive — alors que le mail dont il
// est question n'existe même pas encore.

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
    height: '100vh',
    boxSizing: 'border-box',
    overflow: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyBase,
  },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: tokens.spacingHorizontalS },
  pending: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
});

function Outcome({ result }: { result: SendAndFileResult }) {
  if (result.status === 'filed') {
    return (
      <MessageBar intent="success">
        <MessageBarBody>
          <MessageBarTitle>Envoyé et rangé</MessageBarTitle>
          {result.subject || 'Le message'} est parti et sa copie est archivée.
        </MessageBarBody>
      </MessageBar>
    );
  }
  if (result.status === 'duplicate') {
    return (
      <MessageBar intent="info">
        <MessageBarBody>
          <MessageBarTitle>Envoyé, déjà présent</MessageBarTitle>
          Le message est parti. Une copie identique était déjà rangée
          {result.existing?.folderName ? ` dans « ${result.existing.folderName} »` : ''} : rien n'a
          été dupliqué.
        </MessageBarBody>
      </MessageBar>
    );
  }
  // « Parti mais pas rangé » : le pire des cas à taire, donc celui qu'on explicite
  // le plus. L'utilisateur doit comprendre qu'il ne faut SURTOUT PAS renvoyer.
  return (
    <MessageBar intent="warning">
      <MessageBarBody>
        <MessageBarTitle>Envoyé, mais pas rangé</MessageBarTitle>
        Le message est bien parti — ne le renvoyez pas. Son archivage a échoué&nbsp;:{' '}
        {result.message}. Vous pouvez le ranger depuis les Éléments envoyés.
      </MessageBarBody>
    </MessageBar>
  );
}

export function SendAndFileView() {
  const styles = useStyles();
  const [dest, setDest] = useState<Destination>(EMPTY_DESTINATION);
  const touchRecent = useRecentProjectsStore((s) => s.touch);
  const sendAndFile = useSendAndFile();

  const result = sendAndFile.data;
  // Une fois le mail parti, il n'y a plus rien à envoyer : le formulaire disparaît
  // au profit du compte rendu. Laisser le bouton actif inviterait à un second
  // envoi du même message.
  const done = Boolean(result);

  const ready = isDestinationReady(dest);

  const onSend = () => {
    if (!ready || !dest.containerId) return;
    touchRecent(dest.containerId);
    sendAndFile.mutate({
      targetFolderId: dest.folderId,
      targetContainerId: dest.containerId,
    });
  };

  return (
    <div className={styles.root}>
      <Text size={400} weight="semibold">
        Envoyer et ranger
      </Text>

      {!done && (
        <>
          <Text className={styles.hint}>
            Le message sera d'abord envoyé, puis c'est sa copie des Éléments envoyés qui sera
            archivée — celle dont les en-têtes sont définitifs.
          </Text>

          <DestinationPicker value={dest} onChange={setDest} />

          {sendAndFile.isError && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>Rien n'a été envoyé</MessageBarTitle>
                {sendAndFile.error.message}. Votre brouillon est intact, vous pouvez réessayer.
              </MessageBarBody>
            </MessageBar>
          )}

          {sendAndFile.isPending ? (
            <div className={styles.pending}>
              <Spinner size="tiny" />
              <Text className={styles.hint}>
                Envoi, puis recherche de la copie envoyée… cela peut prendre quelques secondes.
              </Text>
            </div>
          ) : (
            <div className={styles.actions}>
              <Button appearance="primary" onClick={onSend} disabled={!ready}>
                Envoyer et ranger
              </Button>
            </div>
          )}
        </>
      )}

      {result && (
        <>
          <Outcome result={result} />
          <Text className={styles.hint}>
            La fenêtre du message affiche encore l'ancien brouillon&nbsp;: l'envoi est passé par le
            serveur, pas par Outlook. Refermez-la avec le bouton ci-dessous plutôt que par la croix,
            pour éviter qu'Outlook ne propose d'en réenregistrer un brouillon.
          </Text>
          <div className={styles.actions}>
            <Button appearance="primary" onClick={() => void closeComposeWindow()}>
              Fermer la fenêtre du message
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
